export interface AssetIdentityScanTag {
    ID: string;
    View360ID?: string;
    ViewName?: string;
    Target3D?: IVector3D;
}

export interface AssetIdentityScanGroup<T extends AssetIdentityScanTag> {
    scanID: string;
    scanName: string;
    tag: T;
}

/** Return one representative tag for each scan, preferring a requested tag. */
export const groupAssetTagsByScan = <T extends AssetIdentityScanTag>(
    tags: readonly T[],
    preferredTagID?: string,
): AssetIdentityScanGroup<T>[] => {
    const groups = new Map<string, AssetIdentityScanGroup<T>>();

    for (const tag of tags) {
        if (!tag.View360ID) continue;

        const existing = groups.get(tag.View360ID);
        if (!existing || tag.ID === preferredTagID) {
            groups.set(tag.View360ID, {
                scanID: tag.View360ID,
                scanName: tag.ViewName ?? '',
                tag,
            });
        }
    }

    return Array.from(groups.values());
};

export const formatIndexedMenuLabel = (
    baseLabel: string,
    index: number,
    visibleCount: number,
    totalCount: number,
    overflowSuffix: string,
): string => {
    if (totalCount <= 1) return baseLabel;

    const isTruncationMarker = totalCount > visibleCount && index === visibleCount - 1;
    return `${baseLabel} (${isTruncationMarker ? overflowSuffix : index + 1})`;
};

export interface AssetIdentityNavigationMenuItem {
    id: string;
    label: string;
    iconClass?: string;
    hasSeparatorBefore?: boolean;
    execute: () => void | Promise<void>;
}

export interface AssetIdentityNavigationLabels {
    flyTo3DAsset: string;
    flyTo3DAssetInRangeView: string;
    flyTo3DTag: string;
    flyToTagInRangeView: string;
    focusTag: string;
    openScanPosition: string;
    overflowSuffix: string;
}

export interface AssetIdentityNavigationCallbacks<TTag extends AssetIdentityScanTag, TAsset extends { ID: string }> {
    focusCurrentTag?: (tag: TTag) => void | Promise<void>;
    openScanPosition: (tag: TTag) => void | Promise<void>;
    flyTo3DAsset: (asset: TAsset) => void | Promise<void>;
    flyTo3DTag: (tag: TTag) => void | Promise<void>;
}

export const buildAssetIdentityNavigationMenuItems = <TTag extends AssetIdentityScanTag, TAsset extends { ID: string }>(options: {
    surface: 'range' | 'direct' | 'pano';
    assets: readonly TAsset[];
    panoTags: readonly TTag[];
    threeDTags: readonly TTag[];
    currentViewID?: string;
    preferredTagID?: string;
    labels: AssetIdentityNavigationLabels;
    callbacks: AssetIdentityNavigationCallbacks<TTag, TAsset>;
}): AssetIdentityNavigationMenuItem[] => {
    const items: AssetIdentityNavigationMenuItem[] = [];
    const appendGroup = (groupItems: AssetIdentityNavigationMenuItem[]) => {
        if (groupItems.length > 0) {
            groupItems[0].hasSeparatorBefore = items.length > 0;
            items.push(...groupItems);
        }
    };

    const assetLimit = options.surface === 'range' || options.surface === 'pano' ? 2 : 5;
    const visibleAssets = options.assets.slice(0, assetLimit);
    const assetItems = visibleAssets.map((asset, index) => ({
            id: `identity-3d-asset-${asset.ID}`,
            label: formatIndexedMenuLabel(
                options.surface === 'range' || options.surface === 'pano'
                    ? options.labels.flyTo3DAssetInRangeView
                    : options.labels.flyTo3DAsset,
                index,
                visibleAssets.length,
                options.assets.length,
                options.labels.overflowSuffix,
            ),
            iconClass: 'new-view-focus-icon',
            execute: () => options.callbacks.flyTo3DAsset(asset),
        }));

    const scanGroups = groupAssetTagsByScan(options.panoTags, options.preferredTagID);
    const scanItems = scanGroups.map(group => ({
        id: `identity-scan-${group.scanID}`,
        label: `${options.labels.openScanPosition} '${group.scanName}'`,
        iconClass: 'view-focus-icon',
        execute: () => options.callbacks.openScanPosition(group.tag),
    }));

    // A tag identity can have many 3D occurrences, but the viewer menu cannot
    // distinguish them before navigation. Keep the viewer shortcut focused on
    // the first tag; detailed per-tag navigation is available after entering 3D.
    const visibleTags = options.threeDTags.slice(0, 1);
    const tagItems = visibleTags.map((tag, index) => ({
            id: `identity-3d-tag-${tag.ID}`,
            label: formatIndexedMenuLabel(
                options.surface === 'pano' ? options.labels.flyToTagInRangeView : options.labels.flyTo3DTag,
                index,
                visibleTags.length,
                visibleTags.length,
                options.labels.overflowSuffix,
            ),
            iconClass: 'new-view-focus-icon',
            execute: () => options.callbacks.flyTo3DTag(tag),
        }));

    if (options.surface === 'pano') {
        const currentTags = options.panoTags
            .filter(tag => tag.View360ID === options.currentViewID)
            .sort((left, right) => {
                if (left.ID === options.preferredTagID) return -1;
                if (right.ID === options.preferredTagID) return 1;
                return 0;
            });
        const currentTagItems = currentTags.map((tag, index) => ({
            id: `identity-focus-tag-${tag.ID}`,
            label: formatIndexedMenuLabel(
                options.labels.focusTag,
                index,
                currentTags.length,
                currentTags.length,
                options.labels.overflowSuffix,
            ),
            iconClass: 'view-focus-icon',
            execute: () => options.callbacks.focusCurrentTag?.(tag) ?? undefined,
        }));
        appendGroup([
            ...currentTagItems,
            ...scanItems.filter(item => !item.id.endsWith(`-${options.currentViewID}`)),
        ]);
        appendGroup(assetItems);
        appendGroup(tagItems);
    } else {
        appendGroup(assetItems);
        appendGroup(scanItems);
        appendGroup(tagItems);
    }

    return items;
};
